/**
 * WHAT CENTRELINK WOULD MAKE OF THE ARRANGEMENT, as step 1 is filled in (owner, 24 Oct 2026: with a build of
 * $600,000 entered, step 1 still said "Centrelink's value of the right $398,316 … pay more than that in cash and
 * the excess is a gift" — true only of cash).
 *
 * The same arithmetic as the shared formula (shared/src/calculation/grannyFlatArrangement.ts,
 * getGrannyFlatDeprivation — pinned equal by tests/gfCentrelinkView.test.ts), taking Centrelink's formula
 * value as given, so step 1 can redraw as they type without a calculation call:
 *
 *  - the home signed over, or the cost of a build, ON ITS OWN is taken at face value: that is the value of the
 *    right, and none of it is a gift, whatever it is worth;
 *  - cash, or cash on top of the home or a build, is tested: the right can be valued up to the greater of the
 *    formula value ("the limit") and the home or build; whatever is given above that is a gift;
 *  - a gift counts for five years, less what can be given freely ($10,000 a year);
 *  - homeowner status turns on the total given (cash + home + build), as the calc reads it
 *    (shared householdPosition: above the extra allowable amount, homeowners and the right is not counted; at
 *    or below it, non-homeowners, and the right counts as an asset).
 */
import { EXTRA_ALLOWABLE_AMOUNT, GIFTING_LIMIT_ANNUAL } from '../../constants/index.ts';

export type GfForm = 'unsure' | 'cash' | 'build' | 'home';

export interface GfArrangementFigures {
    form: GfForm;
    cash: number;
    build: number;
    home: number;
    /** Everything given for the right. */
    totalGiven: number;
    /** Centrelink's formula value — the most cash can buy without a gift. */
    limit: number;
    /** What the right can be valued at: the greater of the limit and the home or build given. */
    valueCeiling: number;
    /** What the right is valued at: what was given, less any gift. */
    valueOfRight: number;
    /** Given above the ceiling — a gift before the free area. */
    giftGross: number;
    /** The part of the gift Centrelink counts, after the $10,000 a year that can be given freely. */
    giftCounted: number;
    /** Above the extra allowable amount: homeowners, and the right is not counted. */
    homeowner: boolean;
    homeownerLine: number;
}

export function gfArrangementFigures(args: { form: GfForm; cash?: number; build?: number; home?: number; limit: number }): GfArrangementFigures {
    const form = args.form;
    const cash = form === 'unsure' ? 0 : Math.max(0, args.cash || 0);
    const build = form === 'build' ? Math.max(0, args.build || 0) : 0;
    const home = form === 'home' ? Math.max(0, args.home || 0) : 0;
    const limit = Math.max(0, args.limit || 0);
    const totalGiven = cash + build + home;
    const valueCeiling = Math.max(limit, home, build);
    const giftGross = Math.max(0, totalGiven - valueCeiling);
    return {
        form, cash, build, home, totalGiven, limit, valueCeiling,
        valueOfRight: totalGiven - giftGross,
        giftGross,
        giftCounted: Math.max(0, giftGross - GIFTING_LIMIT_ANNUAL),
        homeowner: totalGiven > EXTRA_ALLOWABLE_AMOUNT,
        homeownerLine: EXTRA_ALLOWABLE_AMOUNT,
    };
}
